import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {selectGameStartedAt, selectGameType, selectPlayers} from '../../redux/reducers/sharedStateReducer/selectors';
import {selectLobbyId} from '../../redux/reducers/privateStateReducer/selectors';
import {GAME_TYPES} from '../../constants/game';
import store from '../../redux/store';
import {REACTION_RECIPIENT_ALL, REACTION_TYPES} from '../../configs/reactions';
import {getRandomElement} from '../../utils/other/getRandomElement';
import {getRandomDelayMs} from '../../utils/other/getRandomDelayMs';
import {sendBotReaction} from '../../utils/reactions/sendBotReaction';
import {scheduleBotReply} from '../../utils/reactions/scheduleBotReply';
import {checkIfBotSendsReactions} from '../../utils/reactions/checkIfBotSendsReactions';
import {getBotReactionRecipientIds} from '../../utils/reactions/getBotReactionRecipientIds';

const RANDOM_REACTION_MIN_DELAY_MS = 0;
const RANDOM_REACTION_MAX_DELAY_MS = 10 * 60 * 1000;
const RANDOM_REACTION_CHANCE = 0.3;

const GREETING_MIN_DELAY_MS = 5 * 1000;
const GREETING_MAX_DELAY_MS = 3 * 60 * 1000;
const GREETING_CHANCE = 0.6;
const GREETING_REACTION_TYPE_IDS = ['peace_offer', 'alliance_offer', 'greeting', 'angel', 'suspicious'];

// Простой детерминированный хэш строки -> [0, 1). Не криптографический — нужна
// только устойчивая псевдослучайность: один и тот же seed ВСЕГДА дает один и
// тот же результат, сколько бы раз клиент ни перезагружал страницу. Используется
// только для приветствия (см. decideGreeting) — обычный Math.random() для
// повторяющегося цикла НЕ трогаем, там сброс при перезагрузке нужен по ТЗ
const seededRandom01 = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (Math.imul(hash, 31) + seed.charCodeAt(i)) | 0;
  }
  return (hash >>> 0) / 0xFFFFFFFF;
};

// Решение по приветствию бота — детерминированное функция от lobbyId+botId,
// поэтому одинаковое на любом клиенте и не меняется между перезагрузками
// страницы одного и того же матча. null — бот не выиграл 50%-бросок
const decideGreeting = (lobbyId, botId) => {
  const willGreet = seededRandom01(`${lobbyId}:${botId}:greeting-chance`) < GREETING_CHANCE;
  if (!willGreet) {
    return null;
  }
  const delayFraction = seededRandom01(`${lobbyId}:${botId}:greeting-delay`);
  const delayMs = GREETING_MIN_DELAY_MS
    + Math.floor(delayFraction * (GREETING_MAX_DELAY_MS - GREETING_MIN_DELAY_MS + 1));
  const reactionType = GREETING_REACTION_TYPE_IDS[
    Math.floor(seededRandom01(`${lobbyId}:${botId}:greeting-reaction`) * GREETING_REACTION_TYPE_IDS.length)
    ];
  return {delayMs, reactionType};
};

// Симуляция случайных реакций от лица ботов в сетевом матче
// (openspec/changes/reaction-messages). Запускается независимо на стороне
// КАЖДОГО реального клиента — игрока или зрителя (у ботов нет собственного
// клиента, кому-то из реальных нужно сделать это за них). Сервер принимает
// переопределение fromPlayerId в SEND_REACTION, только если оно ссылается
// на реального бота этого же лобби (см. server/index.js) — выдать себя за
// игрока таким образом нельзя.
//
// Два независимых механизма:
// 1. Повторяющийся цикл на все время матча: случайная пауза (см.
//    RANDOM_REACTION_MIN/MAX_DELAY_MS), по ее истечении — с вероятностью
//    RANDOM_REACTION_CHANCE (30%) случайный бот отправляет случайную
//    реакцию случайному адресату (включая «Всем» как равновероятный
//    вариант наравне с игроками; явным ботам — никогда, см.
//    getBotReactionRecipientIds); при оставшихся 70% в этот раз просто
//    ничего не отправляется. Новая пауза запускается ВСЕГДА (100%),
//    независимо от того, отправилась реакция или нет — цикл никогда не
//    останавливается сам по себе. Намеренно НЕ детерминирован — каждая
//    перезагрузка страницы должна заново запускать случайный интервал (см.
//    ТЗ). cooldown обычных реакций (reactionCooldown.js) сюда намеренно не
//    подключается — он про анти-спам ручной отправки текущим пользователем,
//    а не про эти фоновые отправки за ботов.
// 2. Одноразовое приветствие в начале матча: для КАЖДОГО бота независимо, с
//    вероятностью 50%, через случайные 15 секунд - 3 минуты ОТ СТАРТА
//    МАТЧА (gameStartedAt = момент START_GAME, не момент монтирования этого
//    компонента) — реакция из ограниченного дружелюбного набора, отправленная
//    всем. В отличие от механизма 1, здесь бросок/задержка/тип реакции
//    детерминированы (decideGreeting, seed = lobbyId+botId) — перезагрузка
//    страницы НЕ переигрывает решение и не сдвигает окно: клиент просто
//    досчитывает оставшееся до уже решенного момента, а если тот уже
//    прошел — молча пропускает (не досылает задним числом)
//
// После КАЖДОЙ отправленной здесь реакции (оба механизма) этот же клиент —
// и только он — дополнительно зовет scheduleBotReply (та же утилита, что и
// ответ бота на реакцию реального игрока в ReactionsPanel): с той же
// вероятностью 40% и той же задержкой 10-30 секунд другой бот может
// ответить. Рекурсивно, но не убегает: ответ бота адресуется обратно
// исходному отправителю, а раз это тоже бот — цепочка способна на пару
// обменов подряд (геометрическое затухание), тогда как ответ на реакцию
// РЕАЛЬНОГО игрока обрывается сразу же (отвечать больше некому — адресат
// не бот)
const BotReactionsSimulator = () => {
  const gameType = useSelector(selectGameType);
  const lobbyId = useSelector(selectLobbyId);
  const gameStartedAt = useSelector(selectGameStartedAt);
  // Примитивное число, а не сам players — иначе эффекты ниже пересоздавались
  // бы (и заново дергали таймеры) на любое несвязанное изменение игрового
  // состояния. Стабилизируется, как только состав игроков загрузится
  // Считаем только тех ботов, что вообще шлют реакции: матч, где все боты
  // «явные», не должен крутить пустой цикл и заводить приветствия
  const botCount = useSelector((state) => selectPlayers(state).filter(checkIfBotSendsReactions).length);

  // Механизм 1: повторяющийся цикл. Специально ОТДЕЛЬНЫЙ эффект от
  // приветствия (механизм 2) — у них разные зависимости и разная семантика
  // перезагрузки (этот — сбрасывается заново, см. комментарий класса выше;
  // приветствие — нет, ему нужен gameStartedAt, который тут не нужен)
  useEffect(() => {
    if (gameType !== GAME_TYPES.ONLINE || !lobbyId || botCount === 0) {
      return;
    }

    let cancelled = false;
    const timerIds = [];

    const scheduleNextRandomReaction = () => {
      const delay = getRandomDelayMs(RANDOM_REACTION_MIN_DELAY_MS, RANDOM_REACTION_MAX_DELAY_MS);
      const timerId = setTimeout(() => {
        if (cancelled) {
          return;
        }
        const players = selectPlayers(store.getState());
        const bots = players.filter(checkIfBotSendsReactions);
        if (bots.length > 0 && Math.random() < RANDOM_REACTION_CHANCE) {
          const bot = getRandomElement(bots);
          const toPlayerId = getRandomElement(getBotReactionRecipientIds(players, bot.id));
          sendBotReaction(lobbyId, bot.id, toPlayerId, getRandomElement(REACTION_TYPES).id);
          // Тот же клиент, что отправил эту реакцию за бота, ведет и ее
          // возможную цепочку ответов — другие клиенты по входящей
          // REACTION_RECEIVED ничего подобного не запускают (см. scheduleBotReply)
          scheduleBotReply(lobbyId, toPlayerId, bot.id, () => cancelled);
        }
        scheduleNextRandomReaction();
      }, delay);
      timerIds.push(timerId);
    };
    scheduleNextRandomReaction();

    return () => {
      cancelled = true;
      timerIds.forEach((timerId) => clearTimeout(timerId));
    };
  }, [gameType, lobbyId, botCount]);

  // Механизм 2: приветствие. Решение (выигран ли 50%-бросок, через сколько
  // мс от СТАРТА МАТЧА и какой реакцией) — детерминированная функция от
  // lobbyId+botId (decideGreeting), одна и та же на любом клиенте и при
  // любой перезагрузке страницы этого же матча. При (пере)монтировании
  // просто пересчитываем оставшееся время до уже решенного момента:
  // положительное — ждем остаток; момент уже прошел — молча пропускаем
  // (не досылаем задним числом, повторно решение не перебрасываем)
  useEffect(() => {
    if (gameType !== GAME_TYPES.ONLINE || !lobbyId || botCount === 0 || !gameStartedAt) {
      return;
    }

    let cancelled = false;
    const timerIds = [];

    selectPlayers(store.getState()).filter(checkIfBotSendsReactions).forEach((bot) => {
      const greeting = decideGreeting(lobbyId, bot.id);
      if (!greeting) {
        return;
      }
      const remainingMs = (gameStartedAt + greeting.delayMs) - Date.now();
      if (remainingMs <= 0) {
        return;
      }
      const timerId = setTimeout(() => {
        if (cancelled) {
          return;
        }
        sendBotReaction(lobbyId, bot.id, REACTION_RECIPIENT_ALL, greeting.reactionType);
        scheduleBotReply(lobbyId, REACTION_RECIPIENT_ALL, bot.id, () => cancelled);
      }, remainingMs);
      timerIds.push(timerId);
    });

    return () => {
      cancelled = true;
      timerIds.forEach((timerId) => clearTimeout(timerId));
    };
  }, [gameType, lobbyId, botCount, gameStartedAt]);

  return null;
};

export default BotReactionsSimulator;
